/**
 * Normalizes configuration data based on the specified app type (llu5 or libre1)
 *
 * @param rawConfig - The raw configuration object to be normalized
 * @param country - The country code for which the configuration is being normalized
 * @param app - App type ('llu5' or 'libre1') that determines the normalization strategy
 * @returns A normalized Config object with standardized structure
 * @throws Will throw an error with status 400 if normalization fails or app is not supported
 */
export const normalizeConfig = (rawConfig, country, app) => {
    try {
        switch (app) {
            case 'llu5':
                return normalizeLlu5Config(rawConfig);
            case 'libre1':
                return normalizeLibreConfig(rawConfig, country);
            default:
                throw { status: 400, message: `App not supported: ${app}` };
        }
    }
    catch (error) {
        throw { status: 400, message: `Error normalizing config: ${error || 'Unknown error'}` };
    }
};
const normalizeLlu5Config = (rawConfig) => {
    const llu5Config = rawConfig.base;
    const config = {
        minAge: llu5Config.minCaregiverAge,
        apiCountryUrls: llu5Config.lluAPICountryURLs,
        supportedLanguages: llu5Config.supportedLanguages,
        supportedCountries: Object.entries(llu5Config.supportedCountries).map(([key]) => key),
        countries: [],
        passwordRules: llu5Config.passwordRules,
        documentUrls: {
            files: llu5Config.documentsUrl,
        },
        documentPaths: {
            files: {
                termsOfUse: llu5Config.documents.toullu,
                privacyNotice: llu5Config.documents.pnllu,
            },
        },
        validation: {
            patient: {
                firstNameMaxLength: llu5Config.validationRules?.firstName?.stringMaxLength ?? 40,
                lastNameMaxLength: llu5Config.validationRules?.lastName?.stringMaxLength ?? 80,
            },
        },
    };
    return config;
};
const normalizeLibreConfig = (rawConfig, country) => {
    const passwordRules = rawConfig.validation.identity.map(({ data, type, message }) => ({
        data,
        type,
        message,
    }));
    const config = {
        minAge: rawConfig.adultAges[country],
        apiCountryUrls: rawConfig.libreApiCountryUrls,
        supportedLanguages: rawConfig.languages,
        supportedCountries: rawConfig.supportedCountries,
        countries: rawConfig.countries,
        passwordRules,
        documentUrls: rawConfig.documentUrls,
        documentPaths: {
            cms: { ...rawConfig.documentPaths.cms },
            files: {
                termsOfUse: rawConfig.documentPaths.files.toulibre,
                privacyNotice: rawConfig.documentPaths.files.pnlibre,
            },
        },
        validation: {
            patient: {
                firstNameMaxLength: rawConfig.validation?.libreApi?.patient?.firstName?.stringMaxLength ?? 40,
                lastNameMaxLength: rawConfig.validation?.libreApi?.patient?.lastName?.stringMaxLength ?? 80,
            },
        },
    };
    return config;
};
